import { useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import GameStatsText from '~/statistics-v2/components/gameStatsText'
import { StackNLoadProductAPIStat } from '~/statistics/types/stack-n-load.types'

/**
 * ⚠ `isAllGrades` marks these figures as lifetime while its neighbours are grade-scoped: Stack'n Load
 * stores progress in Firestore as one overwritten doc per level, with no curriculum on it, so there
 * is nothing to filter by. Without the label the numbers read as this grade's.
 */
export default function StatsStackNload({
  isAllGrades,
  stats,
}: {
  isAllGrades?: boolean
  stats?: StackNLoadProductAPIStat
}): React.ReactElement {
  const { t } = useTranslation('statisticsV2')

  const collectedStarsCount = stats?.summary.collectedStarsCount || 0
  const totalStarsCount = stats?.summary.totalStarsCount || 0
  const collectedGoldBoxesCount = stats?.summary.collectedGoldBoxesCount || 0
  const totalGoldBoxesCount = stats?.summary.totalGoldBoxesCount || 0
  const starsText = totalStarsCount ? `${collectedStarsCount}/${totalStarsCount}` : `${collectedStarsCount}`
  const goldBoxesText = totalGoldBoxesCount
    ? `${collectedGoldBoxesCount}/${totalGoldBoxesCount}`
    : `${collectedGoldBoxesCount}`

  const highscore =
    stats?.details.reduce((max, island) => {
      const islandMax = Object.values(island.levels).reduce((islandLevelMax, level) => {
        return Math.max(islandLevelMax, level.score)
      }, 0)
      return Math.max(max, islandMax)
    }, 0) || 0

  return (
    <div className="flex flex-1 flex-col gap-2 bg-purple-midnight rounded border border-purple p-4 min-w-50">
      <div className="flex gap-2 items-center h-12">
        <ResponsiveImage height={40} srcKey="gamification/games/stack-n-load.png" width={40} />
        <div className="flex flex-col">
          <h3 className="text-white text-sm font-bold">{t(`games.stackNload`)}</h3>
          {isAllGrades && <span className="text-xxs text-gray-400">{t('games.allGradesNote')}</span>}
        </div>
      </div>
      <span className="border-b border-purple" />
      <div className="flex flex-col gap-2 py-2">
        <GameStatsText text={t('games.highscore')} value={highscore} />
        <GameStatsText text={t('games.starsCollected')} value={starsText} />
        <GameStatsText text={t('games.goldBoxesSmashed')} value={goldBoxesText} />
      </div>
    </div>
  )
}
